import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ChevronLeft, 
  Save, 
  BookOpen, 
  User, 
  Users,
  Layout,
  X,
  Check,
  Search
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Combobox } from "../components/ui/combobox";
import { MultiSelect } from "../components/ui/multi-select";
import { Badge } from "../components/ui/badge";
import { cn } from "../components/ui/utils";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from "../components/ui/dialog";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { TrainingPlan, TrainingPlanTemplate, Procedure, User as UserType } from "../types";
import { getMatchedObjects, getUser } from "../utils/helpers";
import { getUsersVisibleTocurrentUser } from "../utils/permissions";
import { toast } from "sonner";

const TrainingPlanForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { 
    trainingPlans, 
    procedures, 
    users, 
    selectionUsers,
    trainingPlanTemplates,
    addTrainingPlan, 
    updateTrainingPlan 
  } = useData();

  const isEdit = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Partial<TrainingPlan>>({
    trainingPlanName: "",
    description: "",
    status: "Not Started",
    procedures: []
  });

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState("");

  // Filter visible trainees based on user role (archived users can never be selected)
  const visibleTrainees = getUsersVisibleTocurrentUser(currentUser, users).filter(u => !u.archived);

  const hasProgress = isEdit && (formData.procedureChecks?.some(pc => pc.explained || pc.demonstrated) || false);

  useEffect(() => {
    if (isEdit && id) {
      const plan = trainingPlans.find(p => p.id == id);
      if (plan) {
        if (plan.status === "Completed") {
          toast.error("Completed training plans cannot be edited.");
          navigate(`/training-plans/${id}`);
          return;
        }
        setFormData({
          ...plan,
          // Ensure relations are objects or ids as expected by the form
          trainee: plan.trainee,
          trainer: plan.trainer,
          procedures: plan.procedures || [],
          sourceTemplateId: plan.sourceTemplateId || plan.sourceTemplate?.id
        });
        if (plan.sourceTemplateId || plan.sourceTemplate?.id) {
          setSelectedTemplateId(plan.sourceTemplateId || plan.sourceTemplate?.id);
        }
      }
    }
  }, [isEdit, id, trainingPlans, navigate]);

  const handleTemplateChange = (templateId: string | undefined) => {
    const oldTemplate = trainingPlanTemplates.find(t => t.id === selectedTemplateId);
    const oldTemplateProcIds = oldTemplate?.procedures.map(p => String(p.id)) || [];
    
    setSelectedTemplateId(templateId);
    
    if (!templateId || templateId === "none") {
      setFormData(prev => {
        const manualProcedures = prev.procedures?.filter(p => !oldTemplateProcIds.includes(String(p.id))) || [];
        return {
          ...prev,
          procedures: manualProcedures,
          sourceTemplateId: undefined
        };
      });
      if (templateId === "none") {
        toast.info("Cleared template procedures, keeping manual additions");
      }
      return;
    }

    const template = trainingPlanTemplates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => {
        // Identify procedures that were NOT part of the previous template (manual additions)
        const manualProcedures = prev.procedures?.filter(p => !oldTemplateProcIds.includes(String(p.id))) || [];
        
        // Merge with new template procedures, avoiding duplicates
        const newTemplateProcIds = template.procedures.map(p => String(p.id));
        const finalProcedures = [...template.procedures];
        
        manualProcedures.forEach(proc => {
          if (!newTemplateProcIds.includes(String(proc.id))) {
            finalProcedures.push(proc);
          }
        });

        return {
          ...prev,
          procedures: finalProcedures,
          sourceTemplateId: template.id
        };
      });
      toast.info(`Applied template: ${template.templateName}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    setIsSubmitting(true);
    try {
      const planData = {
        ...formData,
        traineeId: formData.trainee?.id,
        trainerId: formData.trainer?.id,
      };

      if (isEdit && id) {
        await updateTrainingPlan(id, planData);
        navigate(`/training-plans/${id}`);
      } else {
        await addTrainingPlan({
          ...planData,
          createdBy: currentUser,
          createdAt: new Date().toISOString(),
        });
        navigate("/training-plans");
      }
    } catch (error) {
      console.error("Error saving training plan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20 md:pb-0">
      {/* Sticky Mobile Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b p-4 -mx-4 md:mx-0 md:static md:bg-transparent md:border-0 md:p-0 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold md:text-2xl">
            {isEdit ? "Edit Training Plan" : "New Training Plan"}
          </h1>
        </div>
        <Button onClick={handleSubmit} disabled={isSubmitting} className="md:hidden">
          <Save className="w-4 h-4 mr-2" />
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              General Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="planName">Training Plan Name <span className="text-destructive">*</span></Label>
              <Input
                id="planName"
                placeholder="e.g. Front of House Induction"
                value={formData.trainingPlanName || ""}
                onChange={(e) => setFormData({ ...formData, trainingPlanName: e.target.value })}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description <span className="text-destructive">*</span></Label>
              <Textarea
                id="description"
                placeholder="Provide a brief overview of what this training plan covers..."
                value={formData.description || ""}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="min-h-[100px]"
                required
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Assignment
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="trainee">Trainee <span className="text-destructive">*</span></Label>
              <Combobox
                options={Array.from(new Map(visibleTrainees.map(u => [`${u.name}-${u.jobRole}`, u])).values())
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(user => ({
                    label: `${user.name} (${user.jobRole || 'none'})`,
                    value: String(user.id),
                  }))}
                value={formData.trainee?.id ? String(formData.trainee.id) : ""}
                onChange={(value) => setFormData({ ...formData, trainee: value ? getUser(visibleTrainees, value) : undefined })}
                placeholder="Select trainee"
                searchPlaceholder="Search trainee..."
                emptyMessage="No trainee found."
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="trainer">Trainer (Optional)</Label>
              <Combobox
                options={Array.from(new Map(selectionUsers.map(u => [`${u.name}-${u.jobRole}`, u])).values())
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(user => ({
                    label: `${user.name} (${user.jobRole || 'none'})`,
                    value: String(user.id),
                  }))}
                value={formData.trainer?.id ? String(formData.trainer.id) : ""}
                onChange={(value) => setFormData({ ...formData, trainer: value ? getUser(selectionUsers, value) : undefined })}
                placeholder="Select trainer"
                searchPlaceholder="Search trainer..."
                emptyMessage="No trainer found."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <Layout className="w-5 h-5 text-primary" />
              Procedures
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {trainingPlanTemplates.length > 0 && (
              <div className="grid gap-2">
                <Label>Template:</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full md:w-fit justify-between min-w-[200px]"
                  onClick={() => setIsTemplateModalOpen(true)}
                  disabled={hasProgress || isSubmitting}
                >
                  <span className="truncate">
                    {trainingPlanTemplates.find(t => t.id === selectedTemplateId)?.templateName || "None"}
                  </span>
                  <Layout className="w-4 h-4 ml-2 opacity-50 shrink-0" />
                </Button>
                {hasProgress && (
                  <p className="text-xs text-amber-600 font-medium">
                    This Training Plan is In Progress so you cant replace this Training Template/Procedures because some training has been completed.
                  </p>
                )}

                {/* Template Selection Modal */}
                <Dialog open={isTemplateModalOpen} onOpenChange={setIsTemplateModalOpen}>
                  <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0">
                    <DialogHeader className="p-6 pb-2">
                      <DialogTitle>Select Training Plan Template</DialogTitle>
                    </DialogHeader>
                    
                    {/* Modal Search Bar */}
                    <div className="px-6 py-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search templates by name or role..."
                          value={modalSearchTerm}
                          onChange={(e) => setModalSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 pt-4">
                      <div className="grid gap-3">
                        {(!modalSearchTerm || "none".includes(modalSearchTerm.toLowerCase())) && (
                          <div 
                            className={cn(
                              "p-4 border rounded-lg cursor-pointer transition-colors hover:bg-slate-50",
                              (!selectedTemplateId || selectedTemplateId === 'none') ? "border-primary bg-slate-50 ring-1 ring-primary/20" : "border-border"
                            )}
                            onClick={() => {
                              handleTemplateChange("none");
                              setIsTemplateModalOpen(false);
                              setModalSearchTerm("");
                            }}
                          >
                            <div className="flex justify-between items-center">
                              <div className="font-semibold text-sm">None</div>
                              {(!selectedTemplateId || selectedTemplateId === 'none') && <Check className="text-primary w-4 h-4" />}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">Start with an empty procedure list.</p>
                          </div>
                        )}
                        
                        {trainingPlanTemplates
                          .filter(t => t.status === "Active")
                          .filter(t => 
                            t.templateName.toLowerCase().includes(modalSearchTerm.toLowerCase()) ||
                            (t.jobRole?.title && t.jobRole.title.toLowerCase().includes(modalSearchTerm.toLowerCase()))
                          )
                          .map(template => (
                            <div 
                              key={template.id}
                              className={cn(
                                "p-4 border rounded-lg cursor-pointer transition-colors hover:bg-slate-50",
                                selectedTemplateId === template.id ? "border-primary bg-slate-50 ring-1 ring-primary/20" : "border-border"
                              )}
                              onClick={() => {
                                handleTemplateChange(template.id);
                                setIsTemplateModalOpen(false);
                                setModalSearchTerm("");
                              }}
                            >
                            <div className="flex justify-between items-start">
                              <div className="space-y-1.5">
                                <div className="font-semibold text-sm">{template.templateName}</div>
                                {template.jobRole && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-[11px] font-medium text-muted-foreground">Role:</span>
                                    <Badge 
                                      variant="secondary" 
                                      className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-medium w-fit whitespace-nowrap shrink-0 gap-1 bg-gray-200 text-gray-800 text-[11px] md:text-xs border-transparent"
                                    >
                                      <span>{template.jobRole.title}</span>
                                    </Badge>
                                  </div>
                                )}
                              </div>
                              {selectedTemplateId === template.id && <Check className="text-primary w-4 h-4" />}
                            </div>
                            <div className="mt-3">
                              <div className="text-[11px] font-medium mb-2 text-muted-foreground">Included Procedures:</div>
                              <div className="flex flex-wrap gap-1">
                                {template.procedures.map(p => (
                                  <Badge 
                                    key={p.id} 
                                    variant="secondary" 
                                    className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-medium w-fit whitespace-nowrap shrink-0 gap-1 bg-gray-200 text-gray-800 text-[11px] md:text-xs border-transparent"
                                  >
                                    <span>{p.procedureName}</span>
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <DialogFooter className="p-4 border-t bg-slate-50/50">
                      <Button variant="ghost" onClick={() => setIsTemplateModalOpen(false)}>Close</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            <div className="grid gap-2">
              <div className="flex items-center justify-between mb-1">
                <Label>Procedure Selection <span className="text-destructive">*</span></Label>
                <Badge variant="outline">{formData.procedures?.length || 0} Selected</Badge>
              </div>

              {/* Selected Procedures Badges */}
              {formData.procedures && formData.procedures.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2 p-2 border border-dashed border-gray-300 rounded-md bg-slate-50/30">
                  {formData.procedures.map((proc) => {
                    const procCheck = formData.procedureChecks?.find(pc => 
                      (typeof pc.procedure === 'string' ? pc.procedure === String(proc.id) : pc.procedure?.id === proc.id)
                    );
                    const procHasProgress = procCheck?.explained || procCheck?.demonstrated;

                    return (
                      <Badge 
                        key={proc.id} 
                        variant="secondary" 
                        className={cn(
                          "inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-medium w-fit whitespace-nowrap shrink-0 gap-1 text-[11px] md:text-xs border-transparent transition-all",
                          procHasProgress 
                            ? "bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300 border-solid" 
                            : "bg-gray-200 text-gray-800"
                        )}
                      >
                        <span>{proc.procedureName}</span>
                        {!procHasProgress && (
                          <button
                            type="button"
                            onClick={() => {
                              const updated = formData.procedures?.filter(p => p.id !== proc.id) || [];
                              setFormData({ ...formData, procedures: updated });
                            }}
                            className="ml-1 hover:bg-gray-300 rounded-full p-0.5 transition-colors"
                          >
                            <X size={12} strokeWidth={3} />
                          </button>
                        )}
                      </Badge>
                    );
                  })}
                </div>
              )}

              <MultiSelect
                options={procedures.map(proc => {
                  const procCheck = formData.procedureChecks?.find(pc => 
                    (typeof pc.procedure === 'string' ? pc.procedure === String(proc.id) : pc.procedure?.id === proc.id)
                  );
                  return {
                    label: proc.procedureName,
                    value: String(proc.id),
                    isInactive: !!(procCheck?.explained || procCheck?.demonstrated)
                  };
                })}
                selected={formData.procedures?.map(p => String(p.id)) || []}
                onChange={values => {
                  const currentProceduresWithProgress = formData.procedures?.filter(proc => {
                    const procCheck = formData.procedureChecks?.find(pc => 
                      (typeof pc.procedure === 'string' ? pc.procedure === String(proc.id) : pc.procedure?.id === proc.id)
                    );
                    return procCheck?.explained || procCheck?.demonstrated;
                  }) || [];
                  
                  const newSelectedProcedures = getMatchedObjects(values, procedures);
                  
                  // Ensure procedures with progress are always included
                  const finalProcedures = [...newSelectedProcedures];
                  currentProceduresWithProgress.forEach(proc => {
                    if (!finalProcedures.find(p => p.id === proc.id)) {
                      finalProcedures.push(proc);
                    }
                  });
                  
                  setFormData({ ...formData, procedures: finalProcedures });
                }}
                placeholder="Search and add procedures..."
                className="min-h-10"
              />
            </div>
          </CardContent>
        </Card>

        <div className="hidden md:flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="min-w-[150px]">
            {isSubmitting ? "Saving..." : isEdit ? "Update Training Plan" : "Create Training Plan"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TrainingPlanForm;
